import {
  InvalidLengthError,
  LoadingFromUnknownTypeError,
  InvalidSeedLengthError,
  InvalidSecondarySeedLengthError,
  InvalidSaltLengthError,
  InvalidSecretLengthError,
} from './errors';
import { decodeBase64, encodeBase64 } from './util/base64';

export { decodeBase64, encodeBase64 };

export type ArrayOrBase64 = Uint8Array | ArrayBuffer | string;

export function isArrayBufferBacked(u: Uint8Array): u is Uint8Array<ArrayBuffer> {
  return u.buffer instanceof ArrayBuffer;
}

/** Narrow a `Uint8Array<ArrayBufferLike>` (e.g. one decoded from protobuf or zod) to
 * `Uint8Array<ArrayBuffer>` without an unchecked cast. The SharedArrayBuffer branch never
 * actually fires for our inputs, but the fallback keeps this safe by copying into a fresh
 * ArrayBuffer-backed view. */
export function toArrayBufferBacked(u: Uint8Array): Uint8Array<ArrayBuffer> {
  return isArrayBufferBacked(u) ? u : new Uint8Array(u);
}

/** Returns data as Uint8Array, from an ArrayBuffer or a base64 encoded string.
 * Optionally checks for an expected length.
 *
 * The return type is narrowed to `Uint8Array<ArrayBuffer>` because all loaded data is
 * backed by a regular ArrayBuffer (never SharedArrayBuffer). Inputs that happen to be
 * `Uint8Array<ArrayBufferLike>` are passed through unchanged but typed under the
 * narrower brand — this matches actual runtime behavior.
 */
export function loadArrayOrBase64(
  data: ArrayOrBase64,
  expectedLength?: number,
): Uint8Array<ArrayBuffer> {
  if (expectedLength !== undefined) {
    const array = loadArrayOrBase64(data);
    if (array.length !== expectedLength) {
      throw new InvalidLengthError(array.length, expectedLength);
    }
    return array;
  }
  if (data instanceof Uint8Array) {
    return toArrayBufferBacked(data);
  }
  if (typeof data === 'string') {
    return decodeBase64(data);
  }
  // instanceof ArrayBuffer is unreliable, unfortunately :\
  if (data.constructor.name === 'ArrayBuffer' || data instanceof ArrayBuffer) {
    return new Uint8Array(data);
  }
  throw new LoadingFromUnknownTypeError(typeof data);
}

export function concatArrays(...inputs: Uint8Array[]) {
  const totalLength = inputs.map((a) => a.length).reduce((a, b) => a + b, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const input of inputs) {
    result.set(input, offset);
    offset += input.length;
  }
  return result;
}

export function splitArray<L extends number[]>(
  input: Uint8Array,
  ...lengths: L
): { [K in keyof L]: Uint8Array<ArrayBuffer> } {
  const totalLength = lengths.reduce((a, b) => a + b, 0);
  if (input.length !== totalLength) {
    throw new InvalidLengthError(input.length, totalLength);
  }
  const result: Uint8Array<ArrayBuffer>[] = [];
  let offset = 0;
  for (const length of lengths) {
    result.push(input.slice(offset, offset + length));
    offset += length;
  }
  return result as { [K in keyof L]: Uint8Array<ArrayBuffer> };
}

async function sha512(data: Uint8Array<ArrayBuffer>) {
  return new Uint8Array(await crypto.subtle.digest('SHA-512', data));
}

async function hmacSha256(key: Uint8Array<ArrayBuffer>, data: Uint8Array<ArrayBuffer>) {
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  return new Uint8Array(await crypto.subtle.sign('HMAC', cryptoKey, data));
}

export async function deriveSecretFromSeed(
  seed: Uint8Array<ArrayBuffer>,
  secondarySeed: Uint8Array<ArrayBuffer> | null,
  salt: string,
  length = 32,
): Promise<Uint8Array<ArrayBuffer>> {
  if (seed.length !== 32) {
    throw new InvalidSeedLengthError();
  }
  if (secondarySeed !== null && secondarySeed.length !== 32) {
    throw new InvalidSecondarySeedLengthError();
  }
  if (salt.length < 8) {
    throw new InvalidSaltLengthError();
  }
  if (length !== 32 && length !== 64) {
    throw new InvalidSecretLengthError();
  }
  let hashedSeed;
  if (secondarySeed === null) {
    hashedSeed = await sha512(seed);
  } else {
    hashedSeed = await sha512(concatArrays(seed, secondarySeed));
  }
  const hashedData = await sha512(concatArrays(decodeUTF8(salt), hashedSeed));
  return new Uint8Array(hashedData.slice(0, length));
}

/** Derives a secret from a seed using HMAC-SHA-256 as the PRF, suitable for AES-GCM keys.
 *
 * Unlike {@link deriveSecretFromSeed} (which chains plain SHA-512 hashes), this keys HMAC
 * with the seed material and uses the salt as the message. This is a more typical kdf
 * construction for domain separation between salts. Output is the full 32-byte HMAC.
 *
 * NOTE: This produces different bytes than the legacy derivation; it is not a drop-in
 * replacement for existing persisted keys, only for newly derived ones.
 *
 * `length` is currently restricted to 32 (the native HMAC-SHA-256 output size); the argument
 * is kept for signature parity with {@link deriveSecretFromSeed}. */
export async function deriveSecretFromSeedModern(
  seed: Uint8Array<ArrayBuffer>,
  secondarySeed: Uint8Array<ArrayBuffer> | null,
  salt: string,
  length = 32,
): Promise<Uint8Array<ArrayBuffer>> {
  if (seed.length !== 32) {
    throw new InvalidSeedLengthError();
  }
  if (secondarySeed !== null && secondarySeed.length !== 32) {
    throw new InvalidSecondarySeedLengthError();
  }
  if (salt.length < 8) {
    throw new InvalidSaltLengthError();
  }
  if (length !== 32) {
    throw new InvalidSecretLengthError();
  }
  const key = secondarySeed === null ? seed : concatArrays(seed, secondarySeed);
  return hmacSha256(key, decodeUTF8(salt));
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export function encodeUTF8(data: Uint8Array): string {
  return decoder.decode(data);
}

export function decodeUTF8(text: string): Uint8Array<ArrayBuffer> {
  return encoder.encode(text);
}

export function uint8ArrayEqual(x: Uint8Array, y: Uint8Array): boolean {
  if (x.length !== y.length) {
    return false;
  }
  let diff = 0;
  for (let i = 0; i < x.length; i++) {
    // eslint-disable-next-line no-bitwise
    diff |= x[i]! ^ y[i]!;
  }
  return diff === 0;
}

export function randomBytes(count: number): Uint8Array<ArrayBuffer> {
  return crypto.getRandomValues(new Uint8Array(count));
}

export function randomSeed(): Uint8Array<ArrayBuffer> {
  return randomBytes(32);
}
